// Card 08, wrecks (R801, ui-map-interface.md §17.3.08).
//
// A chart marks every wreck on the sea bed, and INT 1 gives it one symbol.
// Here every hull that went down this season leaves one: the server keeps
// the season's wreck list and sends it to every client, because a wreck is
// public (the owner's ruling). The modern reading draws the INT 1 symbol,
// the historical one a ship on her side. The card's dashed hexagon and its
// `Wk` label are not drawn.
//
// **UNDER A BATTLE MARK THE WRECK WAITS.** A hull the viewer lost or sank
// inside the battle marks' window already has its crossed darts (card 52),
// and the wreck under them would be the same fact twice. `liveSinkings` of
// `battle-marks.ts` is the ONE rule of which marks stand, and this piece
// hides the wreck of every hull those marks name: the fleet id of a SINK
// replay, or the cell of a message that carries no such replay. When the
// mark leaves its window, the wreck appears in its place.
//
// **THE LAST DAY ONLY** (R804). The hello carries the wrecks younger than
// `rules.wreckWindowSec`, and the state store drops older ones as new ones
// arrive. The piece cuts at the same age, and `outgrown` asks for a new
// pack when the oldest drawn wreck reaches it, so a wreck leaves the sea
// bed on time on a quiet day that sinks nothing.
//
// **THE NPC SIDE** (v2-the-deep.md §14.5, R2637). A wreck of an NPC hull
// carries its kind (`Wreck.npc`), and the piece draws it in the NPC ink
// (`NPC_INK.color`), so a sunk pirate reads as the NPC side on the sea bed
// too. Every other wreck keeps the ink of its reading.
//
// **THE CARGO BALE** (round 132 card 01, R2847). The cargo of a wreck is
// the viewer's own fact (R2843): a wreck the viewer holds a cargo sighting
// of draws a bale beside its hull, in the ink of its hull (`wreckBale`).
// The modern bale is a square with two cords and a faint fill at
// `fig.bale_fill` of the wreck alpha, one white quad; the historical bale
// is lines alone. Every other wreck draws plain, and so does a wreck with a
// recovered row (R2850). `outgrown` asks for a new pack when
// `state.cargoSightingsRev` moved: a wreck gained or lost its sighting, or
// its row turned recovered. A later hearing of a known wreck moves no
// figure.
//
// **A SMALL MARK** (R2851). The piece answers no `pick`, so
// `commands.clickMap` never finds a wreck. It answers `markPick`: the
// drawn box of a wreck (`WRECK_BOXES` of its reading, with the bale when
// it carries one, times `fig.scale`) holds the point, grown by the touch
// margin of a finger. The tooltip peeks the wreck card on a hover and on a
// press (`fast/smallmarks.ts`). The pick scans the drawn wrecks of the
// window once and allocates nothing.
//
// **THE PACK IS A WINDOW.** A season can sink thousands of hulls, and the
// piece draws only at close zoom. It therefore packs the wrecks inside the
// view grown by `pack_slack` views on every side, and `outgrown` asks for a
// new pack when the view leaves that window or when a hiding mark leaves
// its window. Both are one compare a frame (§10 rule 2).

import { WRECK_BALE_BOX, WRECK_BOXES, wreck, wreckBale } from '../../../render/chart-glyphs';
import { NpcKind, ReplayKind, type InboxMsg, type Wreck } from '../../../gen/proto/aquaphobia';
import { NPC_INK } from '../../../gen/presentation';
import { cellAt, mpxToPx } from '../../../world';
import type { ViewRect } from '../../layer';
import type { SpriteEntry } from '../../layers/batches';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { wreckTakes } from '../../../ui/cards/treasure';
import { boxHolds, offerSmallMark, type SmallMark } from '../../smallmarks';
import { liveSinkings } from './battle-marks';
import { CHART_INK, inkOf, leftRect, slackRect } from './shared';

/**
 * One wreck the last pack wrote; `npc` marks a wreck of the NPC side, and
 * `cargo` a wreck the viewer holds a cargo sighting of (R2847). `w` is the
 * row of the store, which a pick hands to the tooltip (R2851).
 */
interface Drawn { x: number; y: number; npc: boolean; cargo: boolean; w: Wreck }

export class WrecksPiece implements Piece {
  readonly id = 'wrecks' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** A wreck belongs to the season running now, so the film hides it. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'wrecks', 'inbox', 'reading'] as const;

  /** The wrecks of the last pack, inside the window. The tests read them. */
  drawn: Drawn[] = [];

  /** The world rect the last pack covers. */
  private win: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };

  /**
   * The wall second the pack goes stale: the first hiding mark leaves its
   * window, or the oldest drawn wreck reaches the wreck window (R804).
   */
  private expiresAt = Infinity;

  /** The `cargoSightingsRev` of the store at the last pack (R2847). */
  private sightingsRev = -1;

  /** The reading and the scale of the last pack, for the pick (R2851). */
  private modern = true;
  private scale = 1;

  rebuild(ctx: ChartContext): void {
    const t = ctx.cfg.pieces.wrecks;
    this.modern = ctx.reading === 'modern';
    this.scale = t.fig.scale;
    this.win = slackRect(ctx.view, t.fig.pack_slack);
    const hidden = this.hiding(ctx);
    const win = this.win;
    const state = ctx.state;
    // The wreck window (R804). A store with no rules reads 0 and cuts none.
    const span = state.rules.wreckWindowSec;
    const now = state.seasonElapsedSec();
    const cut = span > 0 ? now - span : -Infinity;
    let oldest = Infinity;
    this.sightingsRev = state.cargoSightingsRev;
    this.drawn = [];
    for (const w of state.wrecks) {
      if (w.sunkSec < cut) continue;
      const x = mpxToPx(w.xMpx), y = mpxToPx(w.yMpx);
      if (x < win.x0 || x > win.x1 || y < win.y0 || y > win.y1) continue;
      if (hidden.fleets.has(w.fleet)) continue;
      if (hidden.cells.size > 0 && hidden.cells.has(cellAt(x, y))) continue;
      this.drawn.push({ x, y, npc: w.npc !== NpcKind.NPC_KIND_NONE, cargo: wreckTakes(state, w), w });
      oldest = Math.min(oldest, w.sunkSec);
    }
    if (span > 0 && oldest < Infinity) {
      const leftWall = (oldest + span - now) / Math.max(1e-9, state.timescale);
      this.expiresAt = Math.min(this.expiresAt, Date.now() / 1000 + leftWall);
    }
  }

  /**
   * The hulls and cells the viewer's live battle marks stand on.
   *
   * It also sets `expiresAt`: the wall second the first of those marks
   * leaves its window, when the wreck under it must appear.
   */
  private hiding(ctx: ChartContext): { fleets: Set<number>; cells: Set<number> } {
    const fleets = new Set<number>();
    const cells = new Set<number>();
    this.expiresAt = Infinity;
    // A reading that draws no battle mark hides no wreck.
    if (!ctx.cfg.pieces.battle_marks[ctx.reading].on) return { fleets, cells };
    const nowSec = Date.now() / 1000;
    const window = ctx.cfg.pieces.battle_marks.fig.window_world_sec;
    for (const { msg, ago } of liveSinkings(ctx, nowSec)) {
      if (sunkHulls(msg, fleets) === 0) cells.add(msg.cell);
      const leftWall = (window - ago) / Math.max(1e-9, ctx.state.timescale);
      this.expiresAt = Math.min(this.expiresAt, nowSec + leftWall);
    }
    return { fleets, cells };
  }

  /**
   * Offers the drawn wreck whose box, grown by `margin` world px, holds a
   * world point (R2851): one scan of the window, no allocation.
   */
  markPick(wx: number, wy: number, margin: number, out: SmallMark): void {
    const boxes = this.modern ? WRECK_BOXES.modern : WRECK_BOXES.historical;
    const s = this.scale;
    for (const d of this.drawn) {
      const b = d.cargo ? boxes.cargo : boxes.plain;
      if (!boxHolds(wx, wy, d.x, d.y, b.x0 * s, b.y0 * s, b.x1 * s, b.y1 * s, margin)) continue;
      if (offerSmallMark(out, 'wreck', d.x, d.y, wx, wy)) out.wreck = d.w;
    }
  }

  outgrown(ctx: ChartContext): boolean {
    if (leftRect(ctx.view, this.win)) return true;
    if (ctx.state.cargoSightingsRev !== this.sightingsRev) return true;
    return Date.now() / 1000 > this.expiresAt;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.wrecks;
    const r = t[ctx.reading];
    const modern = ctx.reading === 'modern';
    const ink = inkOf(r.ink, CHART_INK.pale);
    const alpha = t.fig.alpha * r.alpha;
    const s = t.fig.scale;
    const B = WRECK_BALE_BOX;
    let white: SpriteEntry | null = null;
    for (const d of this.drawn) {
      const col = d.npc ? NPC_INK.color : ink;
      wreck(sinks.segments, d.x, d.y, modern, col, alpha, r.width, s);
      if (!d.cargo) continue;
      wreckBale(sinks.segments, d.x, d.y, modern, col, alpha, r.width, s);
      if (!modern) continue;
      // The faint fill of the modern square: the flat white texel, tinted.
      white ??= ctx.glyphs.mark('white');
      sinks.quads.quad(
        d.x + (B.x0 + B.side / 2) * s, d.y + (B.y0 + B.side / 2) * s, 0, 0, B.side * s, B.side * s,
        0, 0, col, alpha * t.fig.bale_fill, white.u0, white.v0, white.u1, white.v1,
      );
    }
  }
}

/**
 * Adds the hulls a sinking message names to `out`, and answers how many.
 *
 * A FLEET_LOST and a FLEET_KILLED message carry a SINK replay whose one
 * fleet is the sunk hull (`hub/combatlog.go` `sinkRef`). A FLEET_KILLED
 * message is the viewer's kill, so its hull is the enemy's, and that
 * wreck waits under the mark too.
 */
function sunkHulls(msg: InboxMsg, out: Set<number>): number {
  const rep = msg.replay;
  if (!rep || rep.kind !== ReplayKind.REPLAY_KIND_SINK) return 0;
  for (const id of rep.fleets) out.add(id);
  return rep.fleets.length;
}

/** The one instance the registry holds. */
export const wrecks = new WrecksPiece();
